

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MainLayout } from "../layout/MainLayout";
import { Search, RefreshCw, Grid3X3, X } from "lucide-react";
import { ActionButton } from "../ui/ActionButton";
import Toast from "../common/Toast";
import { ActiveFilters } from "../functions/ActiveFilters";
import { EnergyLabel } from "../functions/EnergyLabel";
import { ProjectCard } from "../functions/ProjectCard";
import { PageHeader } from "../functions/PageHeader";
import { FilterCard } from "../functions/FilterCard";
import { LoadingState } from "../functions/LoadingState";
import { ErrorState } from "../functions/ErrorState";
import { PageContainer } from "../ui/base-components";
import { Button } from "../ui/base-components";


const CACHE_DURATION = 5 * 60 * 1000;
const CACHE_KEY = "userProjectsCache";

const UserProjectsGrid = ({ 
  title = "Your Projects",
  subtitle = "Manage and edit your digital twin projects with advanced filtering and search capabilities",
  navigateTo = "edit"
}) => {
  const urlParams = new URLSearchParams(window.location.search);
  const urlMode = urlParams.get('mode');
  const finalNavigateTo = urlMode || navigateTo;
  
  const finalTitle = urlMode === 'execute' ? 'Execute Projects' : title;
  const finalSubtitle = urlMode === 'execute' ? 
    'Select a project to execute and run workflows' : 
    subtitle;
    
  const apiRoot = process.env.REACT_APP_API_ROOT;
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [projectIdFilter, setProjectIdFilter] = useState("");
  const [energyLabelFilter, setEnergyLabelFilter] = useState("");
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [forceRefresh, setForceRefresh] = useState(false);
  const [toast, setToast] = useState(null);

  const energyLabels = ["A", "B", "C", "D", "E", "F", "G"];
  const userId = localStorage.getItem("userID");
  const token = localStorage.getItem("token");

  const showToast = (message, type = "info") => {
    setToast({ message, type });
    if (type !== "loading") {
      setTimeout(() => setToast(null), 3000);
    }
  };

  const getCachedData = () => {
    try {
      const cachedData = localStorage.getItem(CACHE_KEY);
      if (!cachedData) return null;

      const { data, timestamp } = JSON.parse(cachedData);
      const isExpired = Date.now() - timestamp > CACHE_DURATION;

      if (isExpired) {
        localStorage.removeItem(CACHE_KEY);
        return null;
      }

      return data;
    } catch (error) {
      console.error("Cache reading error:", error);
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
  };

  const setCachedData = (data) => {
    try {
      const cacheData = { data, timestamp: Date.now() };
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.error("Cache writing error:", error);
    }
  };

  const clearCache = () => {
    try {
      localStorage.removeItem(CACHE_KEY);
    } catch (error) {
      console.error("Cache clearing error:", error);
    }
  };

  const handleRefresh = () => {
    clearCache();
    setForceRefresh((prev) => !prev);
    showToast("Refreshing projects...", "loading");
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setProjectIdFilter("");
    setEnergyLabelFilter("");
    showToast("Filters cleared", "success");
  };

  useEffect(() => {
    const fetchProjects = async () => {
      setIsLoading(true);

      if (!userId || !token) {
        setError("Please log in to view projects");
        setIsLoading(false);
        return;
      }

      const cachedProjects = forceRefresh ? null : getCachedData();
      if (cachedProjects) {
        setProjects(cachedProjects);
        setFilteredProjects(cachedProjects);
        setError(null);
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `${apiRoot}/api/assets/${userId}/get_asset_overview/`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            cache: "no-cache",
          }
        );

        if (!response.ok) {
          if (response.status === 401) {
            clearCache();
            localStorage.removeItem("userID");
            localStorage.removeItem("token");
            navigate("/login");
            throw new Error("Session expired. Please log in again.");
          }
          throw new Error("Failed to fetch projects");
        }

        const data = await response.json();
        const projectsArray = Array.isArray(data) ? data : [];

        if (!forceRefresh) {
          setCachedData(projectsArray);
        }

        setProjects(projectsArray);
        setFilteredProjects(projectsArray);
        setError(null);
        setToast(null);
        showToast("Projects loaded successfully", "success");
      } catch (error) {
        console.error("Error fetching projects:", error);
        setError(error.message || "Failed to load projects. Please try again later.");
        setProjects([]);
        setFilteredProjects([]);
        setToast(null);
        showToast("Failed to load projects", "error");
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjects();
  }, [apiRoot, userId, token, forceRefresh, navigate]);

  useEffect(() => {
    if (!Array.isArray(projects)) {
      setFilteredProjects([]);
      return;
    }

    const filtered = projects.filter((project) => {
      const nameMatch = searchQuery
        ? project?.name?.toLowerCase().includes(searchQuery.toLowerCase())
        : true;

      const idMatch = projectIdFilter
        ? project?.id?.toString().trim().includes(projectIdFilter.trim())
        : true;

      const labelMatch = energyLabelFilter
        ? project?.energy_label === energyLabelFilter
        : true;

      return nameMatch && idMatch && labelMatch;
    });

    setFilteredProjects(filtered);
  }, [searchQuery, projectIdFilter, energyLabelFilter, projects]);

  return (
    <MainLayout>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-8">
        <div className="max-w-7xl mx-auto"> {/* Much wider container */}
          <PageHeader
            icon={Grid3X3}
            title={finalTitle}
            subtitle={finalSubtitle}
          />

          {/* Search and Filters */}
          <FilterCard title="Search & Filter Projects">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search projects..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                />
              </div>

              <input
                type="text"
                placeholder="Filter by Project ID"
                value={projectIdFilter}
                onChange={(e) => setProjectIdFilter(e.target.value)}
                className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
              />

              <select
                value={energyLabelFilter}
                onChange={(e) => setEnergyLabelFilter(e.target.value)}
                className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white"
              >
                <option value="">All Energy Labels</option>
                {energyLabels.map((label) => (
                  <option key={label} value={label}>
                    Energy Label {label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={handleClearFilters}
                variant="secondary"
                disabled={!searchQuery && !projectIdFilter && !energyLabelFilter}
              >
                <X className="w-4 h-4 mr-2" />
                Clear Filters
              </Button>
              
              <ActionButton
                onClick={handleRefresh}
                variant="secondary"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </ActionButton>
            </div>
          </FilterCard>

          {/* Active Filters */}
          <ActiveFilters
            searchQuery={searchQuery}
            projectIdFilter={projectIdFilter}
            energyLabelFilter={energyLabelFilter}
            onClear={handleClearFilters}
          />

          {/* Content */}
          {error && <ErrorState message={error} onRetry={handleRefresh} />}
          
          {isLoading && <LoadingState />}

          {!isLoading && !error && (
            <>
              {filteredProjects.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-8">
                  {filteredProjects.map((project, index) => (
                    <ProjectCard
                      key={project.id || index}
                      name={project.name}
                      description={project.description || project.descriptive_name}
                      imageUrl={project.image_url_value}
                      id={project.id}
                      energyLabel={project.energy_label}
                      navigateTo={finalNavigateTo}
                    />
                  ))}
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-sm text-center py-12 px-6">
                  <Grid3X3 className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-700 mb-2">No Projects Found</h3>
                  <p className="text-slate-500">
                    {searchQuery || projectIdFilter || energyLabelFilter
                      ? "No projects match your current filters. Try adjusting your search criteria."
                      : "You don't have any projects yet. Create your first project to get started."}
                  </p>
                </div>
              )}
            </>
          )}

          {/* Toast Notifications */}
          {toast && (
            <Toast
              message={toast.message}
              type={toast.type}
              onClose={() => setToast(null)}
            />
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default UserProjectsGrid;